/**
 * Темна картка «підписка» — ОДНЕ оформлення на всі місця, де ми ведемо людину
 * на /subscription (Головна і Стрічка).
 *
 * Owner 2026-09-10: «ці два блоки в нашому стилі апки, в нашій дизайн-системі».
 * До цього кожне місце малювало свою картку, і в одному застосунку виходило
 * два оформлення одного й того самого повідомлення:
 *   - заголовок 0.9375 semibold проти 0.875 bold;
 *   - підпис 0.6875 проти 0.75;
 *   - стрілка 36 px проти 32, ще й різного кольору іконки;
 *   - радіус плитки 14 px проти 12, радіус картки 24 проти 16;
 *   - glow різного розміру й прозорості.
 * Плюс головна вада, яку власник побачив у стрічці: рядок був `items-center`,
 * тому плитка й стрілка ставали навпроти СЕРЕДИНИ абзацу, а заголовок лишався
 * зверху — іконка ні з чим не вирівнювалась.
 *
 * За еталон узята картка з Головної (її власник приймав): `rounded-ios-hero`,
 * `bg-dark`, `shadow-ai-card`, плитка `h-11 w-11` з радіусом 14 і власною
 * тінню, кругла оранжева стрілка. Тут вона просто винесена в одне місце й
 * вирівняна за правилами: `p-4`, `gap-3.5`, плитка по ПЕРШОМУ рядку тексту,
 * стрілка по центру картки, багаторядковий текст з балансуванням рядків.
 */
import { Link } from "react-router-dom";
import { Icon, type IconName } from "@/components/Icon";

export function SubscriptionCard({
  icon,
  title,
  text,
  dense = false,
  to = "/subscription",
}: {
  icon: IconName;
  /** Заголовок. Акцентне слово — `<span className="font-serif italic text-primary">`. */
  title: React.ReactNode;
  /** Рядок під заголовком: коротка вигода або обмеження. */
  text: React.ReactNode;
  /**
   * `dense` — підпис в ОДИН рядок (кегль 0.6875rem). Owner 2026-09-10:
   * «Миттєві вакансії + відкриті контакти» — в одну строку. На 12 px цей рядок
   * не влазить у колонку між плиткою і стрілкою навіть на 390 px, тому для
   * коротких підписів лишається менший кегль. Довгий абзац (Стрічка) — 0.75rem.
   */
  dense?: boolean;
  to?: string;
}) {
  return (
    <Link
      to={to}
      className={
        "relative flex gap-3 overflow-hidden rounded-ios-hero bg-dark p-4 text-white shadow-ai-card transition active:scale-[0.99] " +
        // Вирівнювання залежить від того, що ВИЩЕ — текст чи плитка.
        // Короткий підпис (`dense`) нижчий за плитку 44 px, тому при
        // `items-start` текст ставав вище центру плитки — власник
        // 2026-09-10: «текст усередині блока ніхуя не центрований».
        // Довгий абзац (Стрічка) вищий за плитку, і там навпаки: центр
        // відводив плитку до середини абзацу, а заголовок лишався
        // зверху. Тому: короткий — по центру, довгий — по першому рядку.
        (dense ? "items-center" : "items-start")
      }
    >
      {/* Теплий glow у правому нижньому куті — один і той самий в обох місцях. */}
      <div
        className="pointer-events-none absolute -bottom-16 -right-16 h-[12.5rem] w-[12.5rem] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(255,161,53,0.26), rgba(255,161,53,0) 70%)",
        }}
      />
      <div className="relative grid h-11 w-11 shrink-0 place-items-center rounded-[0.875rem] bg-primary text-white shadow-card-soft">
        {/* Рисунок 18 px у плашці 44 → зазор 13 px з усіх боків. */}
        <Icon name={icon} size={16} color="#FFFFFF" />
      </div>
      <div className="relative min-w-0 flex-1">
        <div className="font-display text-[0.9375rem] font-semibold leading-tight [text-wrap:balance]">
          {title}
        </div>
        <div
          className={
            "mt-1 leading-snug text-white/70 [text-wrap:balance] " +
            (dense ? "text-[0.6875rem]" : "text-[0.75rem]")
          }
        >
          {text}
        </div>
      </div>
      <div className="relative grid h-9 w-9 shrink-0 place-items-center self-center rounded-full bg-primary">
        <Icon name="arrow-right" size={16} color="#1a0c02" />
      </div>
    </Link>
  );
}
